export enum ErrorCode {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  AI_ANALYSIS_ERROR = 'AI_ANALYSIS_ERROR',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  NOT_FOUND = 'NOT_FOUND'
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: ErrorCode;
    message: string;
    details?: string[];
  };
  timestamp: string;
}

export interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  timestamp: string;
}

export type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

export class AppError extends Error {
  constructor(
    public code: ErrorCode,
    public message: string,
    public details?: string[]
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export function handleApiError(error: any): AppError {
  console.error('API Error:', error);

  // ネットワークエラー
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new AppError(
      ErrorCode.NETWORK_ERROR,
      'ネットワーク接続を確認してください'
    );
  }

  // レスポンスがある場合
  if (error.response) {
    const status = error.response.status;
    
    switch (status) {
      case 401:
        return new AppError(
          ErrorCode.AUTHENTICATION_ERROR,
          'ログインが必要です'
        );
      case 403:
        return new AppError(
          ErrorCode.AUTHORIZATION_ERROR,
          'アクセス権限がありません'
        );
      case 404:
        return new AppError(
          ErrorCode.NOT_FOUND,
          'リソースが見つかりません'
        );
      case 400:
        return new AppError(
          ErrorCode.VALIDATION_ERROR,
          '入力内容に誤りがあります'
        );
      default:
        return new AppError(
          ErrorCode.INTERNAL_ERROR,
          'サーバーエラーが発生しました'
        );
    }
  }

  // デフォルトエラー
  return new AppError(
    ErrorCode.INTERNAL_ERROR,
    '予期しないエラーが発生しました'
  );
}

export function getErrorMessage(error: AppError): string {
  switch (error.code) {
    case ErrorCode.AUTHENTICATION_ERROR:
      return 'ユーザー名またはパスワードが正しくありません';
    case ErrorCode.NETWORK_ERROR:
      return 'ネットワーク接続を確認してください';
    case ErrorCode.AI_ANALYSIS_ERROR:
      return '画像から情報を読み取れませんでした。手動で入力してください';
    case ErrorCode.VALIDATION_ERROR:
      return error.details?.join('\n') || '入力内容に誤りがあります';
    case ErrorCode.AUTHORIZATION_ERROR:
      return 'アクセス権限がありません';
    case ErrorCode.NOT_FOUND:
      return 'データが見つかりません';
    default:
      return error.message || '予期しないエラーが発生しました';
  }
}

export async function apiCall<T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      // APIエラーレスポンスの場合
      if (data.success === false) {
        throw new AppError(
          data.error.code,
          data.error.message,
          data.error.details
        );
      }
      
      // 通常のHTTPエラー
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // 成功レスポンス
    if (data.success === true) {
      return data.data;
    }

    // レガシーレスポンス（success フィールドなし）
    return data;
  } catch (error) {
    throw handleApiError(error);
  }
}
